import { DropdownMenuItem } from "@webapp/shared/gh-dropdown/gh-dropdown.models";
import { NAV_WIDTH, SCREENS, SUPPORT_SCREENS, TOP_NAV_BAR_PROPS } from "./config";
import { Component, OnInit } from "@angular/core";
import { Ng1StateProxy } from "@gtmhub/shared/services/ng1-state.proxy";
import { ActiveNavScreenService } from "../../services/active-nav-screen.service";
import { RawParams } from "@uirouter/core";
import { HomeService } from "@gtmhub/home/services/home";
import { InsightboardService } from "@gtmhub/insightboards";
import { IReport } from "@gtmhub/reporting/models";
import { setNavWidth } from "../../utils/navigation.util";
import { AuthenticationResolverService } from "@gtmhub/auth";
import { BetaFeaturesService } from "@gtmhub/featureToggle";
import { setTopNavBarProps } from "@gtmhub/shared/components/ui/top-navigation-bar/utils";
import { Screens, IScreen } from "@webapp/navigation/models/shared.models";
import { IColoringSetup, ProgressColoring } from "@gtmhub/configuration";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { GhDropdownMenuItemBuilder } from "@webapp/shared/gh-dropdown/util/gh-dropdown-menu-item-builder";
import { CurrentUserService } from "@gtmhub/users";
import { IProfile } from "@webapp/users/models/users.models";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
@Component({
  selector: "navigation-v2",
  templateUrl: "./navigation.component.html",
  styleUrls: ["./navigation.component.less"],
})
export class NavigationV2Component implements OnInit {
  public screens = SCREENS;
  public supportScreens = SUPPORT_SCREENS;
  public profile: IProfile;
  public activeScreen: Screens;
  public profileMenuItems: DropdownMenuItem[];
  public helpMenuItems: DropdownMenuItem[];
  private paramsMap: Map<Screens, RawParams>;

  constructor(
    private ng1StateProxy: Ng1StateProxy,
    private activeNavStateService: ActiveNavScreenService,
    private homeService: HomeService,
    private insightboardService: InsightboardService,
    private userProfileService: UserProfileService,
    private authenticationResolverService: AuthenticationResolverService,
    private betaFeaturesService: BetaFeaturesService,
    private accountService: AccountService,
    private progressColoring: ProgressColoring,
    private currentUserService: CurrentUserService
  ) {
    setNavWidth({ expanded: NAV_WIDTH.expanded + "px", collapsed: NAV_WIDTH.collapsed + "px" });
    setTopNavBarProps({ height: TOP_NAV_BAR_PROPS.height + "px", borderColor: TOP_NAV_BAR_PROPS.borderColor });
    this.setColoringScale();
    this.setActiveScreen();
  }

  ngOnInit() {
    this.activeNavStateService.getNavStateChange$().subscribe((screen) => {
      this.activeScreen = screen;
    });

    this.insightboardService.getReports().then((reports: IReport[]) => {
      this.homeService.cachedReports = reports;
      this.paramsMap = new Map();
      const performanceReport = this.homeService.cachedReports.find((report) => report.name.toLowerCase() === "performance report");
      this.paramsMap["reports"] = { dashboardId: performanceReport.id };
    });

    this.getCurrentUserDetails();
    this.profileMenuItems = this.getProfileMenuItems();
    this.helpMenuItems = this.getHelpMenuItems();

    const previousSeenNewNaviCount = Number(localStorage.getItem("seenNewNaviCount"));
    const seenNewNaviCount = previousSeenNewNaviCount ? previousSeenNewNaviCount + 1 : 1;
    localStorage.setItem("seenNewNaviCount", JSON.stringify(seenNewNaviCount));
  }

  logout(): void {
    this.authenticationResolverService.logout({ ssoLogoutIfEnabled: true });
  }

  setActive(screen: IScreen): void {
    if (!screen.key) return;
    this.activeScreen = screen.key;
    this.ng1StateProxy.go(screen.state, this.paramsMap[screen.key]);
  }

  getCurrentUserDetails(): void {
    this.profile = this.userProfileService.getProfile();
  }

  async toggleNewExperience(): Promise<void> {
    // old and new nav do not share the same toggle button
    // therefore from the new navigation we always switch to false
    await this.betaFeaturesService.optForBetaFeature("new-experience-toggle", false);
    document.location.reload();
  }

  private setColoringScale(): void {
    const accountColoring = this.accountService.getAccountSetting<IColoringSetup>("coloring");
    this.progressColoring.setColoring(accountColoring);
  }

  private getProfileMenuItems(): DropdownMenuItem[] {
    // Localize
    const menuItemBuilder = new GhDropdownMenuItemBuilder();
    return [
      menuItemBuilder
        .setKey("New Gtmhub")
        .setAction({
          handler: () => this.toggleNewExperience(),
        })
        .setToBeEndOfSection()
        .setUiType({ uiType: "toggle", value: this.getNewGtmhubToggled() })
        .setTrack("New Experience Switch")
        .setTrackMetaData({ direction: "new_to_old" })
        .build(),

      menuItemBuilder.setKey("Edit profile").setAction({ state: "gtmhub.userProfile" }).build(),

      menuItemBuilder.setKey("Manage notifications").setAction({ state: "gtmhub.userNotificationSettings" }).setToBeEndOfSection().build(),

      // Add logout icon
      menuItemBuilder
        .setKey("Logout")
        .setAction({ handler: () => this.logout() })
        .setUiType({ uiType: "icon", icon: "" })
        .build(),
    ];
  }

  private getHelpMenuItems(): DropdownMenuItem[] {
    // Localize
    const menuItemBuilder = new GhDropdownMenuItemBuilder();
    return [
      menuItemBuilder.setKey("Help Center").setUiType({ uiType: "icon", icon: "help" }).setAction({ href: "https://help.gtmhub.com/en/", target: "_blank" }).build(),
      menuItemBuilder
        .setKey("Product updates")
        .setUiType({ uiType: "icon", icon: "manage" })
        .setAction({ href: "https://updates.gtmhub.com/", target: "_blank" })
        .build(),
      menuItemBuilder
        .setKey("Platform status")
        .setUiType({ uiType: "icon", icon: "refresh" })
        .setAction({ href: "https://status.gtmhub.com/", target: "_blank" })
        .build(),
      menuItemBuilder.setKey("Gtmhub blog").setUiType({ uiType: "icon", icon: "edit" }).setAction({ href: "https://gtmhub.com/blog", target: "_blank" }).build(),
      menuItemBuilder
        .setKey("Give feedback")
        .setUiType({ uiType: "icon", icon: "comments" })
        .setAction({ href: "https://gtmhub.atlassian.net/servicedesk/customer/portal/1", target: "_blank" })
        .build(),
    ];
  }

  public goToMarketplace() {
    this.currentUserService.setMarketplaceRedirectCookie();
    window.open("/marketplace", "_self");
  }

  private getNewGtmhubToggled(): boolean {
    // old and new nav do not share the same toggle button therefore
    // from the new navigation the value is always true
    return true;
  }

  private setActiveScreen(): void {
    const allScreens = [...this.screens, ...this.supportScreens];
    const currentState = this.ng1StateProxy.getCurrentState();
    const currentScreen = allScreens.find((s) => currentState.includes(s.state));

    this.activeScreen = currentScreen?.key || "home";
  }
}
